import FileManager from '@modules/files/FileManager';
import { JobApplicationNotesManager } from '@modules/jobApplications/components/JobApplicationNotesManager';
import ScoringExplainerContent from '@modules/scoring/components/ScoringExplainerContent';
import { AIIcon } from '@modules/scoring/components/ScoringScoreBadge';
import { ScoringStatus } from '@modules/scoring/types';
import { NotesManager } from '@modules/talents/components/NotesManager';
import TalentEditDocument from '@modules/talents/TalentEditDocument';
import { Box, Dialog, IconButton, Stack, Theme, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

export interface Note {
    _id: string;
    title: string;
    note: string;
    type: 'public' | 'private';
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    date?: string;
    isSystemGenerated?: boolean;
    aiScore?: number;
}

export interface NoteFormData {
    title: string;
    type: 'public' | 'private' | '';
    note: string;
}

export interface TalentMediaDialogProps {
    isOpen: boolean;
    onClose: () => void;
    talentId: string;
    jobApplicationId?: string;
    talentFavoriteId?: string;
    defaultTab?: 'notes' | 'files' | 'comments' | 'scoring' | 'documents';
    scoreExplainer?: string;
    score?: number | null;
    scoringStatus?: ScoringStatus;
    jobOfferId?: string;
    jobOfferTitle?: string;
    jobOfferNumber?: string | number;
}

export type NoteTypeFilter = 'all' | 'private' | 'public';


const TalentMediaDialog: React.FC<TalentMediaDialogProps> = ({
    isOpen,
    onClose,
    talentId,
    jobApplicationId,
    talentFavoriteId,
    defaultTab = 'notes',
    scoreExplainer,
    score,
    scoringStatus,
    jobOfferId,
    jobOfferTitle,
    jobOfferNumber,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const effectiveDefaultTab = defaultTab === 'scoring' && !jobOfferId ? 'notes' : defaultTab;
    const [activeTab, setActiveTab] = React.useState<'comments' | 'files' | 'notes' | 'scoring' | 'documents'>(effectiveDefaultTab);
    // Update active tab when defaultTab changes
    React.useEffect(() => {
        if (isOpen) {
            setActiveTab(defaultTab === 'scoring' && !jobOfferId ? 'notes' : defaultTab);
        }
    }, [isOpen, defaultTab, jobOfferId]);
    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            maxWidth="lg"
            PaperProps={{
                style: {
                    borderRadius: 10,
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden',
                    minHeight: '80vh',
                    padding: 0,
                    backgroundColor: theme.palette.background.default,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            <IconButton
                sx={{
                    position: 'absolute',
                    right: {
                        xs: 12,
                        sm: 16
                    },
                    top: {
                        xs: 12,
                        sm: 16
                    },
                    zIndex: 1,
                    backgroundColor: theme.palette.background.default,
                }}
                onClick={onClose}>
                <Box component="i" className="fi-br-cross" sx={{
                    fontSize: {
                        xs: 10,
                        sm: 18
                    },
                    color: theme.palette.darkGrey.main,
                }}></Box>
            </IconButton>
            <Stack flexDirection="row" sx={{ height: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <Stack>
                    {/* Tab Icons */}
                    <Stack
                        sx={{
                            alignItems: 'center',
                            borderRight: `1px solid ${theme.palette.border.main}`,
                            backgroundColor: theme.palette.background.default,
                            height: '100%',
                            gap: 1,
                            px: 1,
                            py: 2,
                        }}>
                        <Tooltip title="Notes sur la candidature" placement='right'>
                            <IconButton
                                onClick={() => setActiveTab('notes')}
                                sx={{
                                    backgroundColor: activeTab === 'notes' ? theme.palette.neutralDarkGray.main : 'transparent',
                                    borderRadius: 2
                                }}>
                                <i className="fi-br-book-alt" style={{ fontSize: 18, color: activeTab === 'notes' ? theme.palette.primaryPurple.main : theme.palette.darkGrey.main }}></i>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Fichiers liés à la candidature" placement='right'>
                            <IconButton
                                onClick={() => setActiveTab('files')}
                                sx={{
                                    backgroundColor: activeTab === 'files' ? theme.palette.neutralDarkGray.main : 'transparent',
                                    borderRadius: 2
                                }}>
                                <i className="fi-br-clip" style={{ fontSize: 18, color: activeTab === 'files' ? theme.palette.primaryPurple.main : theme.palette.darkGrey.main }}></i>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Commentaires (disponible prochainement)" placement='right'>
                            <IconButton
                                onClick={(e) => {
                                    // Disabled for now
                                    e.preventDefault();
                                }}
                                sx={{
                                    cursor: 'not-allowed',
                                    backgroundColor: activeTab === 'comments' ? theme.palette.neutralDarkGray.main : 'transparent',
                                    borderRadius: 2,
                                    opacity: 0.5,
                                }}>
                                <i className="fi-br-comments" style={{ fontSize: 18, color: theme.palette.textSecondary.main }}></i>
                            </IconButton>
                        </Tooltip>
                        {jobOfferId && (
                            <Tooltip title="Score IA" placement='right'>
                                <IconButton
                                    onClick={() => setActiveTab('scoring')}
                                    sx={{
                                        backgroundColor: activeTab === 'scoring' ? theme.palette.neutralDarkGray.main : 'transparent',
                                        borderRadius: 2
                                    }}>
                                    <Box sx={{ color: activeTab === 'scoring' ? theme.palette.primaryPurple.main : theme.palette.darkGrey.main, display: 'flex' }}>
                                        <AIIcon />
                                    </Box>
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Documents du talent (CV, ID...)" placement='right'>
                            <IconButton
                                onClick={() => setActiveTab('documents')}
                                sx={{
                                    backgroundColor: activeTab === 'documents' ? theme.palette.neutralDarkGray.main : 'transparent',
                                    borderRadius: 2
                                }}>
                                <i className="fi-br-document" style={{ fontSize: 18, color: activeTab === 'documents' ? theme.palette.primaryPurple.main : theme.palette.darkGrey.main }}></i>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Stack flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
                    <Stack flex={1} sx={{ minHeight: 0, overflowY: 'auto' }}>
                        {activeTab === 'notes' && (
                            jobApplicationId
                                ? <JobApplicationNotesManager
                                    jobApplicationId={jobApplicationId}
                                    variant="embedded"
                                />
                                : <NotesManager
                                    talentId={talentId}
                                    variant="embedded"
                                />
                        )}
                        {activeTab === 'files' && (
                            <FileManager
                                talentId={talentId}
                            />
                        )}
                        {activeTab === 'scoring' && jobOfferId && (
                            <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
                                {/* Description */}
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                                    Ce score IA est calculé sur la base du CV du talent et de l'offre d'emploi.
                                </Typography>

                                {/* Job offer info + Resume file */}
                                <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 2, p: 1.5, borderRadius: 2, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.border.main}` }}>
                                    {(jobOfferTitle || jobOfferNumber) && (
                                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                                            <i className="fi-br-briefcase" style={{ fontSize: 13, color: theme.palette.text.secondary }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {jobOfferTitle}
                                                {jobOfferNumber && (
                                                    <Typography component="span" variant="body2" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>
                                                        N°{jobOfferNumber}
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                                {scoreExplainer ? (
                                    <ScoringExplainerContent html={scoreExplainer} score={score} />
                                ) : (
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Aucun explainer disponible pour ce scoring.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {activeTab === 'documents' && (
                            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                                <TalentEditDocument talentId={talentId} />
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Stack>

        </Dialog>
    );
};

export default TalentMediaDialog;
